/**
 * Rebate Agreement Duplicate Modal
 *
 * Simple modal for duplicating an existing rebate agreement.
 * Only requires name and date range - all other data is copied from source.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import PrimaryButton from 'src/components/common/PrimaryButton';
import CancelButton from 'src/components/common/CancelButton';
import { RebateAgreementListItem } from 'src/api/rebates/types';
import useDuplicateRebateAgreement from 'src/api/rebates/useDuplicateRebateAgreement';
import useRebateAgreements from 'src/api/rebates/useRebateAgreements';

interface RebateAgreementDuplicateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (agreementId?: string) => void;
  sourceAgreement: RebateAgreementListItem | null;
}

const getUtcYear = (dateValue: string) => {
  const parsedDate = new Date(`${dateValue}T00:00:00Z`);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }
  return parsedDate.getUTCFullYear();
};

const isMultiYearAgreement = (agreement: RebateAgreementListItem) => {
  const startYear = getUtcYear(agreement.start_date);
  const endYear = getUtcYear(agreement.end_date);
  if (startYear === null || endYear === null) {
    return false;
  }
  return startYear !== endYear;
};

const formatDateString = (dateValue: Date) => {
  const year = dateValue.getUTCFullYear();
  const month = `${dateValue.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${dateValue.getUTCDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDateByYears = (dateValue: string, yearsToShift: number) => {
  const parsedDate = new Date(`${dateValue}T00:00:00Z`);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }
  parsedDate.setUTCFullYear(parsedDate.getUTCFullYear() + yearsToShift);
  return formatDateString(parsedDate);
};

const getPresetDateRange = (agreement: RebateAgreementListItem) => {
  const startYear = getUtcYear(agreement.start_date);
  const endYear = getUtcYear(agreement.end_date);
  if (startYear === null || endYear === null) {
    return null;
  }

  // Add 1 to ensure the new agreement starts after the original ends
  // e.g., 2024-2027 (4 years) should shift by 4 to get 2028-2031
  const yearShift = Math.max(1, endYear - startYear + 1);
  const presetStart = shiftDateByYears(agreement.start_date, yearShift);
  const presetEnd = shiftDateByYears(agreement.end_date, yearShift);

  if (!presetStart || !presetEnd) {
    return null;
  }

  return {
    startDate: presetStart,
    endDate: presetEnd,
  };
};

const doesAgreementOverlapYear = (
  agreement: RebateAgreementListItem,
  year: number,
) => {
  const startDate = new Date(`${agreement.start_date}T00:00:00Z`);
  const endDate = new Date(`${agreement.end_date}T00:00:00Z`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return false;
  }
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const yearEnd = new Date(Date.UTC(year, 11, 31));
  return startDate <= yearEnd && endDate >= yearStart;
};

const RebateAgreementDuplicateModal: React.FC<
  RebateAgreementDuplicateModalProps
> = ({ open, onClose, onSuccess, sourceAgreement }) => {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string>('');

  const targetYear = useMemo(() => {
    if (!startDate) {
      return null;
    }
    return getUtcYear(startDate);
  }, [startDate]);

  const { data: targetYearAgreements } = useRebateAgreements(
    targetYear !== null ? { years: [targetYear] } : undefined,
    targetYear !== null,
  );

  const duplicateAgreement = useDuplicateRebateAgreement();
  const isLoading = duplicateAgreement.isLoading;

  const warningMessages = useMemo(() => {
    const warnings: string[] = [];
    if (!sourceAgreement) {
      return warnings;
    }

    if (isMultiYearAgreement(sourceAgreement)) {
      warnings.push(
        'Source agreement spans multiple years. Review the date range before duplicating.',
      );
    }

    if (targetYear !== null && targetYearAgreements) {
      const hasExistingAgreement = targetYearAgreements.some(
        (agreement) =>
          agreement.supplier_id === sourceAgreement.supplier_id &&
          doesAgreementOverlapYear(agreement, targetYear),
      );

      if (hasExistingAgreement) {
        warnings.push(
          `Supplier already has an agreement in ${targetYear}. Duplicating may create overlap.`,
        );
      }
    }

    return warnings;
  }, [sourceAgreement, targetYear, targetYearAgreements]);

  const applyPresetDateRange = useCallback(
    (agreement: RebateAgreementListItem) => {
      const presetRange = getPresetDateRange(agreement);
      if (!presetRange) {
        return;
      }

      setStartDate(presetRange.startDate);
      setEndDate(presetRange.endDate);
    },
    [],
  );

  // Prepopulate form when source agreement changes
  useEffect(() => {
    if (sourceAgreement) {
      const presetRange = getPresetDateRange(sourceAgreement);
      const presetEndYear = presetRange
        ? getUtcYear(presetRange.endDate)
        : null;
      const fallbackEndYear = getUtcYear(sourceAgreement.end_date);
      const nameYear = presetEndYear ?? fallbackEndYear ?? undefined;

      setName(
        `${sourceAgreement.name}${nameYear ? ` (${nameYear} Copy)` : ' (Copy)'}`,
      );
      applyPresetDateRange(sourceAgreement);
      setError('');
    }
  }, [applyPresetDateRange, sourceAgreement]);

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Agreement name is required.');
      return;
    }

    if (!startDate) {
      setError('Start date is required.');
      return;
    }

    if (!endDate) {
      setError('End date is required.');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end <= start) {
      setError('End date must be after start date.');
      return;
    }

    if (!sourceAgreement) {
      setError('No source agreement selected.');
      return;
    }

    try {
      const result = await duplicateAgreement.mutateAsync({
        sourceAgreementId: sourceAgreement.id,
        name: name.trim(),
        startDate,
        endDate,
      });

      console.log('Duplication successful:', result);

      // Success - close modal and notify parent
      if (onSuccess) {
        onSuccess(result?.data?.id);
      }
      handleClose();
    } catch (err) {
      console.error('Duplication error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to duplicate agreement. Please try again.',
      );
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setName('');
      setStartDate('');
      setEndDate('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Duplicate Rebate Agreement</DialogTitle>
          {sourceAgreement && (
            <p className="text-sm text-gray-600 mt-2">
              Creating a copy of{' '}
              <span className="font-semibold">{sourceAgreement.name}</span>
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {warningMessages.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm font-medium text-amber-900">Warning</p>
              <ul className="mt-2 list-disc list-inside text-sm text-amber-800">
                {warningMessages.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="agreement-name">
              Agreement Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="agreement-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter agreement name"
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">
                Start Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end-date">
                End Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          {sourceAgreement && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Date range is editable and can be adjusted.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyPresetDateRange(sourceAgreement)}
                disabled={isLoading}
              >
                Apply last term
              </Button>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-800">
              All rebate rules, product groups, and settings will be copied from
              the source agreement.
            </p>
          </div>
        </div>

        <DialogFooter>
          <CancelButton onClick={handleClose} disabled={isLoading}>
            Cancel
          </CancelButton>
          <PrimaryButton onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                Duplicating...
              </>
            ) : (
              'Duplicate Agreement'
            )}
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RebateAgreementDuplicateModal;
